import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { CdkDragDrop, moveItemInArray, CdkDropList, CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { PlanogramLevel } from '../../models/planogram-level';
import { NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
    selector: 'app-planogram-levels',
    templateUrl: './planogram-levels.component.html',
    styleUrl: './planogram-levels.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [
        CdkDropList,
        CdkDrag,
        NgIf,
        CdkDragHandle,
        FormsModule,
    ],
})
export class PlanogramLevelsComponent implements OnInit {
  @Input() levels: PlanogramLevel[] = [];
  @Output() levelsChanged = new EventEmitter<PlanogramLevel[]>();

  // Internal collapsed state management
  private isCollapsed: { [key: string]: boolean } = {};
  
  // Internal working array without Variant
  draggableLevels: PlanogramLevel[] = [];
  variantLevel: PlanogramLevel | null = null;

  ngOnInit(): void {
    this.setupDraggableLevels();
    this.initializeCollapsedState();
  }

  private setupDraggableLevels(): void {
    // Extract variant and create draggable levels without it
    this.variantLevel = this.levels.find(l => l.type === 'Variant') || null;
    this.draggableLevels = this.levels.filter(l => l.type !== 'Variant');
  }

  private initializeCollapsedState(): void {
    // Initialize all levels as collapsed
    this.levels.forEach(level => {
      this.isCollapsed[level.type] = true;
    });
  }

  private rebuildLevelsWithVariant(): PlanogramLevel[] {
    const result: PlanogramLevel[] = [];
    
    for (const level of this.draggableLevels) {
      result.push(level);
      
      // Insert Variant right after Brand
      if (level.type === 'Brand' && this.variantLevel) {
        result.push(this.variantLevel);
      }
    }
    
    return result;
  }

  drop(event: CdkDragDrop<any>) {
    const draggedLevel = event.item.data as PlanogramLevel;
    
    // Only Brand and Height can move
    if (!this.isDraggable(draggedLevel)) {
      return;
    }

    // Can't move above Category (position 0)
    if (event.currentIndex === 0) {
      return;
    }

    // Validate the drop would result in a valid order
    if (!this.isValidDropPosition(draggedLevel, event.currentIndex)) {
      return;
    }

    // Simple move in the draggable array
    moveItemInArray(this.draggableLevels, event.previousIndex, event.currentIndex);

    // Rebuild the full levels array with Variant properly positioned
    const newLevels = this.rebuildLevelsWithVariant();
    
    // Update the original levels array
    this.levels = newLevels;
    
    this.levelsChanged.emit(newLevels);
  }

  private isValidDropPosition(draggedLevel: PlanogramLevel, targetIndex: number): boolean {
    // Create a test copy of draggable levels
    const testLevels = [...this.draggableLevels];
    
    // Simulate the move
    moveItemInArray(testLevels, 
      testLevels.findIndex(l => l.type === draggedLevel.type), 
      targetIndex
    );
    
    return this.isValidLevelOrder(testLevels);
  }

  private isValidLevelOrder(testLevels: PlanogramLevel[]): boolean {
    // Category must be at position 0
    if (testLevels.length === 0 || testLevels[0].type !== 'Category') {
      return false;
    }
    
    // Find positions of core levels
    const segmentPos = testLevels.findIndex(l => l.type === 'Segment');
    const familyPos = testLevels.findIndex(l => l.type === 'Family');
    const basePos = testLevels.findIndex(l => l.type === 'Base');
    
    // Check that Segment → Family → Base are in correct relative order (if they exist)
    if (segmentPos !== -1 && familyPos !== -1 && segmentPos >= familyPos) {
      return false;
    }
    if (familyPos !== -1 && basePos !== -1 && familyPos >= basePos) {
      return false;
    }
    if (segmentPos !== -1 && basePos !== -1 && segmentPos >= basePos) {
      return false;
    }
    
    return true;
  }

  toggleConfig(level: PlanogramLevel) {
    // Toggle the collapsed state for this level type
    this.isCollapsed[level.type] = !this.isCollapsed[level.type];
  }

  isDraggable(level: PlanogramLevel): boolean {
    return level.type === 'Brand' || level.type === 'Height';
  }

  isConfigurable(level: PlanogramLevel): boolean {
    return level.type === 'Brand' || level.type === 'Height' || level.type === 'Variant';
  }

  // For template access
  get displayVariantLevel(): PlanogramLevel | null {
    return this.variantLevel;
  }

  // Helper to check if a level is collapsed
  isLevelCollapsed(levelType: string): boolean {
    return this.isCollapsed[levelType];
  }

  setOrderingDirection(level: PlanogramLevel, updateEvent: Event) {
    if (updateEvent == null || typeof updateEvent == 'undefined') {
      return;
    }
    const selectedOption: number = parseInt(
      (updateEvent.target as HTMLSelectElement).value
    );
    
    // Update the level directly
    level.orderingDirection = selectedOption;
    
    // Rebuild and emit the updated levels
    const newLevels = this.rebuildLevelsWithVariant();
    this.levels = newLevels;
    this.levelsChanged.emit(newLevels);
  }

  setOrderingMethod(level: PlanogramLevel, updateEvent: Event) {
    if (updateEvent == null || typeof updateEvent == 'undefined') {
      return;
    }
    const selectedOption: number = parseInt(
      (updateEvent.target as HTMLSelectElement).value
    );
    
    // Update the level directly
    level.orderingMethod = selectedOption;
    
    // Rebuild and emit the updated levels
    const newLevels = this.rebuildLevelsWithVariant();
    this.levels = newLevels;
    this.levelsChanged.emit(newLevels);
  }
}